import {
  computed,
  onBeforeUnmount,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue'

import type { ChatStreamPhase } from '@/composables/useEventStream'

export interface IChatWaitLabelOptions {
  /** Latest `stream_progress` milestone; absent until the chat stream reports one. */
  streamPhase?: MaybeRefOrGetter<ChatStreamPhase | undefined>
  /** True while reasoning chunks are streaming from the model. */
  isReasoning?: MaybeRefOrGetter<boolean | undefined>
  /** True after an `llm_retry`, until the model answers again. */
  isRetrying?: MaybeRefOrGetter<boolean | undefined>
  /** Image generation shows a placeholder instead of words, so nothing rotates. */
  isImageGeneration?: MaybeRefOrGetter<boolean | undefined>
  /**
   * When the rotating copy started (epoch ms). Indicators given the same value read the same
   * point in it, so a wait that moves to another indicator, or resumes after a step, carries on
   * rather than starting over. Without it the copy starts from the top each time it rotates.
   */
  rotationStartedAt?: MaybeRefOrGetter<number | undefined>
}

/**
 * The label the user reads while waiting.
 *
 * `context_ready` and `calling_model` are emitted back to back by the API with no awaited
 * work between them, so `context_ready` never paints — it is mapped for completeness, not
 * because it is observable. The two preparation phases deliberately share one label: the
 * indicator should read as two steps, not three.
 */
const STREAM_PHASE_LABELS: Record<ChatStreamPhase, string> = {
  request_received: 'Preparing',
  context_ready: 'Preparing',
  calling_model: 'Thinking',
}

/**
 * Cosmetic labels shown while the model is thinking, purely so a long wait keeps showing signs
 * of life. The first entry is the real one — it is what paints for every response that arrives
 * before the first swap, and it must stay truthful on its own.
 *
 * Each label carries its own dwell rather than sharing one interval. A fixed cadence turns into
 * a metronome the user starts anticipating, which reads as a spinner dressed up in words;
 * irregular dwells read as steps that genuinely took different amounts of time. The values stay
 * inside the 3.8–5.4s band — below that the swap starts to feel restless, above it stops
 * registering as activity — and lean longer for labels with more to read.
 */
const WAIT_LABELS = [
  // Held longest: most responses start streaming before it lapses, so no swap ever paints.
  { text: 'Thinking', dwellMs: 5400 },
  { text: 'Gathering context', dwellMs: 3800 },
  { text: 'Processing', dwellMs: 4600 },
  { text: 'Putting it together', dwellMs: 4200 },
  { text: 'Crafting the answer', dwellMs: 5000 },
  { text: 'Working on it', dwellMs: 4400 },
]

/**
 * Taken over once a full pass of `WAIT_LABELS` (27.4s) is spent and the user needs telling it is
 * not stuck. The dwells run well past the earlier band — 8–13s rather than 4–5s — because by this
 * point the user has stopped watching for progress, and a cadence that keeps up the earlier pace
 * reads as agitated rather than reassuring.
 *
 * The tail is long on purpose: website and slide-deck generation regularly run past a minute, and
 * the indicator has to keep showing signs of life for that whole stretch rather than settling on
 * one frozen word 40s in. Between them these labels cover the wait out to ~79s.
 *
 * The arc only ever escalates — each label concedes more time than the one before it, so the copy
 * never walks backwards ("Almost there" → "This one is taking a while") the way a cycle would.
 * The last entry deliberately carries no dwell: it holds until the response arrives, so it has to
 * be something that is still true five minutes in.
 */
const LONG_WAIT_LABELS = [
  { text: 'Still working on it', dwellMs: 8200 },
  { text: 'Hang tight', dwellMs: 9400 },
  { text: 'This one is taking a while', dwellMs: 11000 },
  { text: 'Bigger tasks take a little longer', dwellMs: 12600 },
  { text: 'Almost there', dwellMs: 10400 },
  { text: 'Thanks for your patience' },
]

/** One-way sequence: it advances to the final label and stays there. */
const LABEL_SEQUENCE: { text: string; dwellMs?: number }[] = [
  ...WAIT_LABELS,
  ...LONG_WAIT_LABELS,
]

/** When each label comes up, in ms from the start of the rotation. */
const LABEL_STARTS_MS = LABEL_SEQUENCE.map((_, index) =>
  LABEL_SEQUENCE.slice(0, index).reduce(
    (totalMs, { dwellMs }) => totalMs + (dwellMs ?? 0),
    0,
  ),
)

/** The label that is up `elapsedMs` into the rotation. Clamped, so it only ever moves forward. */
const labelIndexAt = (elapsedMs: number) =>
  Math.max(
    0,
    LABEL_STARTS_MS.findLastIndex((startMs) => startMs <= elapsedMs),
  )

/**
 * What a chat's wait indicator reads, rotating through cosmetic copy while the wait has no
 * real milestone to report. Owns a timer, so call it from a component's setup: the timer stops
 * when that component unmounts. A fresh mount starts the sequence from the top unless it is
 * given `rotationStartedAt`.
 */
export function useChatWaitLabel(options: IChatWaitLabelOptions = {}) {
  const streamPhase = computed(() => toValue(options.streamPhase))
  const isReasoning = computed(() => !!toValue(options.isReasoning))
  const isRetrying = computed(() => !!toValue(options.isRetrying))
  const isImageGeneration = computed(() => !!toValue(options.isImageGeneration))
  const rotationStartedAt = computed(() => toValue(options.rotationStartedAt))

  /**
   * Only the generic "thinking" wait rotates. `Preparing`, `Reasoning` and the retry notice are
   * real milestones reported by the stream, so padding them with cosmetic copy would bury an
   * actual signal.
   */
  const isRotating = computed(
    () =>
      !isImageGeneration.value &&
      !isReasoning.value &&
      !isRetrying.value &&
      (!streamPhase.value || streamPhase.value === 'calling_model'),
  )

  /** Where the rotation is in `LABEL_SEQUENCE`. */
  const labelIndex = ref(0)

  let rotationTimer: ReturnType<typeof setTimeout> | undefined

  const rotatingLabel = computed(() => LABEL_SEQUENCE[labelIndex.value])

  const statusLabel = computed(() => {
    if (isReasoning.value) return 'Reasoning'
    if (isRetrying.value) return 'Model is busy, retrying'
    if (isRotating.value) return rotatingLabel.value.text
    return streamPhase.value
      ? STREAM_PHASE_LABELS[streamPhase.value]
      : 'Thinking'
  })

  const stopRotation = () => {
    clearTimeout(rotationTimer)
    rotationTimer = undefined
  }

  /**
   * Puts up the label that is due `startedAt` into the rotation and schedules the next swap.
   * Each label decides how long it stays up, so there is no shared beat to lock onto. The final
   * one has no dwell: nothing is scheduled after it, so it stays put until the response lands.
   */
  const showLabelDueSince = (startedAt: number) => {
    const elapsedMs = Date.now() - startedAt
    labelIndex.value = labelIndexAt(elapsedMs)
    const nextStartMs = LABEL_STARTS_MS[labelIndex.value + 1]
    if (nextStartMs === undefined) return

    rotationTimer = setTimeout(
      () => showLabelDueSince(startedAt),
      nextStartMs - elapsedMs,
    )
  }

  /**
   * Restarted — not merely resumed — each time the rotation comes back on, so a genuine
   * milestone (`Preparing` → `Thinking`) is never overwritten by a cosmetic word a moment later.
   * Changes that keep it rotating, like the phase being cleared once text arrives, leave it
   * running. Given `rotationStartedAt`, a restart picks up from that point instead of the top.
   */
  const syncRotation = () => {
    stopRotation()
    if (!isRotating.value) return
    showLabelDueSince(rotationStartedAt.value ?? Date.now())
  }

  watch([isRotating, rotationStartedAt], syncRotation, { immediate: true })

  onBeforeUnmount(stopRotation)

  return { statusLabel }
}
